import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AuthLayout from '../../components/layout/AuthLayout';

const sectionHeaderSx = {
  fontWeight: 600,
  color: '#fff',
  mt: 3,
  mb: 1,
};

const bodyTextSx = {
  color: 'rgba(255,255,255,0.8)',
  lineHeight: 1.7,
  mb: 2,
};

const listSx = {
  color: 'rgba(255,255,255,0.8)',
  lineHeight: 1.7,
  pl: 2,
  mb: 2,
  '& li': {
    mb: 0.5,
  },
  '& li::marker': {
    color: 'rgba(255,255,255,0.5)',
  },
};

const dividerSx = {
  borderColor: 'rgba(255,255,255,0.1)',
  my: 2,
};

const tableCellSx = {
  color: 'rgba(255,255,255,0.8)',
  borderColor: 'rgba(255,255,255,0.1)',
  fontSize: '0.8rem',
  py: 1.5,
};

const tableHeaderCellSx = {
  color: '#fff',
  fontWeight: 600,
  borderColor: 'rgba(255,255,255,0.15)',
  fontSize: '0.8rem',
  py: 1.5,
};

const PrivacyPolicyPage: React.FC = () => {
  return (
    <AuthLayout maxWidth="800px">
      <Card
        sx={{
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
          bgcolor: 'rgba(20, 20, 30, 0.58)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
      >
        <CardContent sx={{ px: 5, py: 4 }}>
          {/* Header */}
          <Typography variant="h4" fontWeight={700} color="#fff" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
            Last updated: May 2026
          </Typography>
          <Divider sx={dividerSx} />

          {/* Section 1: Who We Are */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            1. Who We Are
          </Typography>
          <Typography variant="body2" sx={bodyTextSx}>
            Praxis is a hands-on cybersecurity training platform operated by CyberArk Software Ltd.
            (&ldquo;CyberArk&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). This policy explains how we collect, use,
            and protect your personal data when you use Praxis.
          </Typography>

          <Divider sx={dividerSx} />

          {/* Section 2: What Data We Collect */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            2. What Data We Collect
          </Typography>
          <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small" sx={{ bgcolor: 'transparent' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderCellSx}>Data</TableCell>
                  <TableCell sx={tableHeaderCellSx}>When Collected</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Purpose</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={tableCellSx}>Name (first, last)</TableCell>
                  <TableCell sx={tableCellSx}>Registration / enrollment</TableCell>
                  <TableCell sx={tableCellSx}>Identify you in the platform</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={tableCellSx}>Email address</TableCell>
                  <TableCell sx={tableCellSx}>Registration / enrollment</TableCell>
                  <TableCell sx={tableCellSx}>Account login, send verification codes, session invitations</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={tableCellSx}>Password (hashed)</TableCell>
                  <TableCell sx={tableCellSx}>Registration / password reset</TableCell>
                  <TableCell sx={tableCellSx}>Authenticate your account (stored as irreversible hash)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={tableCellSx}>Session progress</TableCell>
                  <TableCell sx={tableCellSx}>During training sessions</TableCell>
                  <TableCell sx={tableCellSx}>Track your learning progress</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={tableCellSx}>Feedback ratings</TableCell>
                  <TableCell sx={tableCellSx}>After session completion</TableCell>
                  <TableCell sx={tableCellSx}>Improve training quality</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={tableCellSx}>IP address</TableCell>
                  <TableCell sx={tableCellSx}>Automatically on access</TableCell>
                  <TableCell sx={tableCellSx}>Security logging, rate limiting</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider sx={dividerSx} />

          {/* Section 3: Legal Basis for Processing */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            3. Legal Basis for Processing
          </Typography>
          <Typography variant="body2" sx={bodyTextSx}>
            We process your data under Article 6(1)(b) of the GDPR &mdash; it is necessary for the performance of a
            contract (providing you with training services you signed up for). We do not rely on consent for core
            service delivery. For security logging, we rely on Article 6(1)(f) &mdash; legitimate interest in
            protecting our platform.
          </Typography>

          <Divider sx={dividerSx} />

          {/* Section 4: How We Use Your Data */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            4. How We Use Your Data
          </Typography>
          <Typography variant="body2" sx={{ ...bodyTextSx, mb: 1 }}>
            We use your data to:
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>Provide and maintain the training platform</li>
            <li>Authenticate your identity and secure your account</li>
            <li>Send you session invitations and verification codes</li>
            <li>Track and display your learning progress</li>
            <li>Generate aggregated, anonymized analytics for instructors</li>
            <li>Detect and prevent abuse or unauthorized access</li>
          </Box>
          <Typography variant="body2" sx={{ ...bodyTextSx, mb: 1, fontWeight: 600 }}>
            We do NOT:
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>Sell your personal data to third parties</li>
            <li>Use your data for advertising or marketing</li>
            <li>Profile you for automated decision-making</li>
            <li>Share individual progress data with third parties</li>
          </Box>

          <Divider sx={dividerSx} />

          {/* Section 5: Data Retention */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            5. Data Retention
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>
              <strong style={{ color: '#fff' }}>Active accounts:</strong> Your data is retained for as long as your
              account is active.
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Deleted accounts:</strong> Upon account deletion, your personal data is
              removed within 30 days. Anonymized analytics (aggregated session statistics) may be retained
              indefinitely.
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Inactive accounts:</strong> Accounts with no login activity for 12
              months may be flagged for review.
            </li>
          </Box>

          <Divider sx={dividerSx} />

          {/* Section 6: Data Sharing */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            6. Data Sharing
          </Typography>
          <Typography variant="body2" sx={{ ...bodyTextSx, mb: 1 }}>
            Your data may be shared with:
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>
              <strong style={{ color: '#fff' }}>Amazon Web Services (AWS):</strong> Our hosting provider. Data is
              stored in the US-West-2 (Oregon) region. AWS acts as a data processor under standard contractual
              clauses.
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Your instructor:</strong> Can see your name, email, session progress,
              and feedback (aggregated when fewer than 2 responses).
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Platform administrators:</strong> Can see your account information for
              support and management purposes.
            </li>
          </Box>
          <Typography variant="body2" sx={bodyTextSx}>
            We do not share data with any other third parties.
          </Typography>

          <Divider sx={dividerSx} />

          {/* Section 7: Your Rights */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            7. Your Rights
          </Typography>
          <Typography variant="body2" sx={{ ...bodyTextSx, mb: 1 }}>
            Under GDPR, you have the right to:
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>
              <strong style={{ color: '#fff' }}>Access:</strong> Request a copy of all personal data we hold about you
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Rectification:</strong> Correct inaccurate personal data
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Deletion:</strong> Delete your account and associated personal data
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Data portability:</strong> Receive your data in a structured,
              machine-readable format
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Object:</strong> Object to processing based on legitimate interest
            </li>
            <li>
              <strong style={{ color: '#fff' }}>Restriction:</strong> Request restriction of processing in certain
              circumstances
            </li>
          </Box>
          <Typography variant="body2" sx={bodyTextSx}>
            To exercise these rights, use the &ldquo;Delete Account&rdquo; option in your profile settings, or
            contact us at the address below.
          </Typography>

          <Divider sx={dividerSx} />

          {/* Section 8: Data Security */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            8. Data Security
          </Typography>
          <Typography variant="body2" sx={{ ...bodyTextSx, mb: 1 }}>
            We protect your data using:
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>Encryption in transit (TLS/HTTPS)</li>
            <li>Encryption at rest (AWS managed encryption)</li>
            <li>Password hashing (bcrypt, irreversible)</li>
            <li>Rate limiting and brute-force protection</li>
            <li>Role-based access controls</li>
            <li>Regular security audits</li>
          </Box>

          <Divider sx={dividerSx} />

          {/* Section 9: Cookies */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            9. Cookies
          </Typography>
          <Typography variant="body2" sx={{ ...bodyTextSx, mb: 1 }}>
            Praxis uses only essential cookies:
          </Typography>
          <Box component="ul" sx={listSx}>
            <li>
              <strong style={{ color: '#fff' }}>Session token:</strong> Stored as an HTTP-only authorization header
              (JWT). Required for authentication.
            </li>
          </Box>
          <Typography variant="body2" sx={bodyTextSx}>
            We do not use tracking cookies, analytics cookies, or third-party cookies.
          </Typography>

          <Divider sx={dividerSx} />

          {/* Section 10: Changes to This Policy */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            10. Changes to This Policy
          </Typography>
          <Typography variant="body2" sx={bodyTextSx}>
            We may update this policy from time to time. If we make material changes, we will notify you via email or
            a prominent notice on the platform. Continued use after changes constitutes acceptance.
          </Typography>

          <Divider sx={dividerSx} />

          {/* Section 11: Contact */}
          <Typography variant="h6" sx={sectionHeaderSx}>
            11. Contact
          </Typography>
          <Typography variant="body2" sx={bodyTextSx}>
            For data protection inquiries:
          </Typography>
          <Box component="ul" sx={{ ...listSx, listStyle: 'none', pl: 1 }}>
            <li>Email: privacy@cyberark.com</li>
            <li>CyberArk Software Ltd.</li>
            <li>9 Hapsagot Street, Park Ofer 2</li>
            <li>Petah Tikva 4951040, Israel</li>
          </Box>

          <Divider sx={dividerSx} />

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <MuiLink
              component={Link}
              to="/login"
              sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline', '&:hover': { color: '#fff' } }}
            >
              &larr; Back to Login
            </MuiLink>
          </Box>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default PrivacyPolicyPage;
